import type {
  CompanyProfile as CompanyProfileFromGraph,
  GroupedCompanies,
  Reviews as ReviewsFromGraph,
} from '@seek/ca-graphql-schema/types';
import { type Jobs, type Reviews } from '@seek/libs-shared';

import type { CompaniesListModel } from '../../../companyProfiles/models/companiesListModel';
import type { LegacySearchCompaniesResult } from '../legacyCompanyProfileService/types';

import type {
  SearchCompaniesResult,
  SearchCompanyProfileApiResult,
  SearchCompanyProfileResult,
} from './types';

export const mapReviewsModel = (
  companyProfile: CompanyProfileFromGraph,
  companyReviews: ReviewsFromGraph | undefined,
): Reviews => ({
  name: companyProfile.name,
  companyId: companyProfile.id,
  rating: companyProfile.reviewsSummary?.overallRating
    ? {
        average: companyProfile.reviewsSummary.overallRating.value,
        count:
          companyProfile.reviewsSummary.overallRating.numberOfReviews.value,
      }
    : undefined,
  reviews: companyReviews?.data?.map((r) => ({
    title: r.title || undefined,
    createdAt: r.createdAt.shortAbsoluteLabel,
    pros: r.pros,
    cons: r.cons,
    overallRating: r.overallRating,
    id: r.id,
    companyId: r.companyId,
    jobTitle: r.jobTitle || undefined,
    employmentStatus: r.employmentStatus || undefined,
    workLocation: r.workLocation || undefined,
    salarySummary: r.salarySummary,
    upvotes: r.upvotes || undefined,
    recommended: r.recommended,
    ratings: r.ratings,
    reply: r.reply
      ? {
          companyLogoUrl: companyProfile.branding?.logo || undefined,
          createdAt: r.reply.createdAt.dateTime,
          from: r.reply.from,
          text: r.reply.text,
          user: {
            userId: r.reply.userId,
          },
        }
      : undefined,
  })),
  paging: {
    page: companyReviews?.paging?.page,
    perPage: companyReviews?.paging?.perPage,
    total: companyReviews?.paging?.total,
    totalPages: companyReviews?.paging?.totalPages,
  },
});

// Legacy Company Profile Service does the mapping for now
export const mapJobsModel = (jobs: Jobs): Jobs => jobs;

export const mapSearchCompanyModel = (
  companySearchResults: LegacySearchCompaniesResult,
): SearchCompaniesResult => ({
  companies: companySearchResults?.data?.map((company) => ({
    logoUrl: company.logo_url,
    name: company.company_name,
    slug: company.company_name_slug,
    overallRating: {
      average: company.average_company_rating,
      count: company.reviews_count,
    },
  })),
  totalCompanies: companySearchResults.paging.total,
});

export const mapCompanyGroupsToFlatCompanies = (
  groupedCompanies?: GroupedCompanies[],
): CompaniesListModel =>
  groupedCompanies?.flatMap((group) =>
    group.companies.map((company) => ({
      name: company.name,
      slug: company.slug,
      id: company.id,
    })),
  ) ?? [];

export const mapSearchCompanyApiResultToSearchCompanyResult = (
  searchCompanyProfileApiResult?: SearchCompanyProfileApiResult,
): SearchCompanyProfileResult =>
  searchCompanyProfileApiResult?.map((companyProfile) => ({
    ...companyProfile,
    data: {
      ...companyProfile.data,
      branding: {
        brandId: companyProfile.data.branding?.brandId,
        coverImageUrl: companyProfile.data.branding?.cover,
        logoImageUrl: companyProfile.data.branding?.logo,
      },
    },
  })) ?? [];
